/**
 * pilot-flags.ts — the single source of truth for the runtime `window.*` pilot
 * flags the PDF editor / a11y-view surfaces read (→ A.24 second-surface: name the
 * fact once, hold it in the compiler).
 *
 * Each flag is a boolean the QA/GO probe can flip in a LIVE browser (no rebuild)
 * to move a surface between its shipped control path (flag OFF, the default) and
 * a pilot path (flag ON). Because a mis-typed flag name silently disables the
 * pilot with NO error, the name lives here ONCE and every reader imports it —
 * before this module `ADA_PDF_PAGE_IMAGE_PILOT` was duplicated across
 * `pdf-view-adapter.ts` and `a11y-view-boot.ts` (pilot F3 / fold F5).
 *
 * No DOM globals are touched at module scope — every reader takes an optional
 * `win` so the module stays import-safe + unit-testable without a browser.
 *
 * @module editor/pilot-flags
 */
/**
 * The `pictures-not-pages` READ-ONLY pilot: serve each PDF page as a backend PNG
 * (`GET /api/pdf-page-image`) through the shared a11y-view mount, instead of the
 * pdf.js canvas. Read by `pdf-view-adapter.ts` (the view adapter) and
 * `a11y-view-boot.ts` (the served-page boot). Epic pdf-editor-pictures-not-pages-260731.
 */
export const PDF_PAGE_IMAGE_PILOT_FLAG = "ADA_PDF_PAGE_IMAGE_PILOT";
/**
 * The EDITABLE `/editor` picture-render port (pdfjs-editor-removal-cutover Phase 2,
 * chunk 2a): render the editor's page raster from backend `PdfPageImageCache` PNGs
 * (at {@link EDITOR_PICTURE_RENDER_DPI}) instead of the pdf.js canvas, keying the
 * overlay coordinate frame off the C4-correct rotation-swapped CropBox dims of that
 * raster. DEFAULT OFF — flag OFF is byte-identical pdf.js, so there is zero
 * live-user risk until a later phase flips the default. Read by
 * `editor-render-strategy.ts`.
 */
export const EDITOR_PICTURE_RENDER_FLAG = "ADA_EDITOR_PICTURE_RENDER";
function readWin(win) {
    if (win)
        return win;
    return typeof window !== "undefined" ? window : undefined;
}
/** True when `window.ADA_PDF_PAGE_IMAGE_PILOT === true` (default false). */
export function pdfPageImagePilotEnabledByGlobal(win) {
    return readWin(win)?.ADA_PDF_PAGE_IMAGE_PILOT === true;
}
/** True when `window.ADA_EDITOR_PICTURE_RENDER === true` (default false). */
export function editorPictureRenderEnabledByGlobal(win) {
    return readWin(win)?.ADA_EDITOR_PICTURE_RENDER === true;
}
